import React from "react";
import { InlineMath, BlockMath } from "react-katex";

import GraphViewer from "../../../../components/GraphPlotter/GraphViewer";
import FractionInput from "../../../../components/inputs/FractionInput";
import {
  samplePolynomialCurve,
} from "./helpers/smoothGraph";

/* =============================================================
   TASK: ReadFunctionValueFromGraphTask
   Cel: Odczytaj wartość funkcji f(a) z wykresu.

   Założenia:
   - wykres jak w maturalnym otwartym (ciągła łamana): segmenty AL-B oraz B-CR
   - jeden, jednoznaczny odczyt (to jest funkcja)
   - a jest całkowite i leży w dziedzinie (uwzględniamy otwarte/domknięte końce)
   - wartości w punktach całkowite (a całkowite + parametry całkowite)
============================================================= */

const TOL = 1e-6;

function randInt(min, max) {
  return Math.floor(Math.random() * (max - min + 1)) + min;
}

function pickClosedFlag() {
  return randInt(0, 1) === 1;
}

function lerp(xa, ya, xb, yb, x) {
  return ya + ((yb - ya) * (x - xa)) / (xb - xa);
}

function eq(a, b) {
  return Math.abs(a - b) < TOL;
}

/**
 * Budujemy wykres w stylu "V" / łamana z jednym załamaniem:
 *  - lewy odcinek: (xL, yL) -> (h, v)
 *  - prawy odcinek: (h, v) -> (xR, yR)
 *
 * Żeby odczyt był prosty i zawsze całkowity:
 *  - nachylenia całkowite (1 lub 2, ze znakiem)
 *  - h, v całkowite
 *  - a całkowite
 *  => f(a) całkowite.
 */
function buildData() {
  for (let attempt = 0; attempt < 1000; attempt++) {
    // zakres
    const xL0 = randInt(-5, -3);
    const xR0 = randInt(3, 5);
    const h = randInt(-1, 1);

    if (!(xL0 < h && h < xR0)) continue;

    const leftClosed = pickClosedFlag();
    const rightClosed = pickClosedFlag();

    // wybieramy "wierzchołek" (punkt załamania)
    const v = randInt(-3, 3);

    // nachylenia (jak w maturze często): 1 lub 2
    const mLeftAbs = randInt(1, 2);
    const mRightAbs = randInt(1, 2);

    // losowo wybieramy kierunki na odcinkach (żeby nie było ciągle tak samo)
    // lewy: m1 = ±mLeftAbs, prawy: m2 = ±mRightAbs, ale niech nie będą oba 0.
    // unikamy sytuacji "prawie poziomo" jeśli nie chcesz – ale tu jest OK.
    const m1 = (randInt(0, 1) === 0 ? -1 : 1) * mLeftAbs;
    const m2 = (randInt(0, 1) === 0 ? -1 : 1) * mRightAbs;

    // funkcje ramion w postaci: y = m(x-h) + v
    const f1 = (x) => m1 * (x - h) + v;
    const f2 = (x) => m2 * (x - h) + v;

    // dobieramy końce dziedziny (czasem przycinamy, żeby mieściło się w yRange)
    let xL = xL0;
    let xR = xR0;

    // policz y na końcach
    let yL = f1(xL);
    let yR = f2(xR);

    // jeśli poza oknem, spróbuj przyciąć
    const inWindow = () =>
      Math.max(Math.abs(yL), Math.abs(v), Math.abs(yR)) <= 5;

    if (!inWindow()) {
      // przytnij xL lub xR do bliżej h, by wartości się zmniejszyły
      // maks 6 prób
      for (let k = 0; k < 6 && !inWindow(); k++) {
        if (Math.abs(yL) > 5 && xL < h - 1) xL += 1;
        if (Math.abs(yR) > 5 && xR > h + 1) xR -= 1;
        yL = f1(xL);
        yR = f2(xR);
      }
      if (!inWindow()) continue;
    }

    // teraz wybieramy argument a (całkowity) z dziedziny,
    // ale uwzględniamy otwarte końce: jeśli xL otwarte, nie bierzemy a=xL, analogicznie xR.
    const possibleA = [];
    for (let a = xL; a <= xR; a++) {
      if (a === xL && !leftClosed) continue;
      if (a === xR && !rightClosed) continue;
      // a może być w h (tam funkcja istnieje) – OK
      possibleA.push(a);
    }
    if (possibleA.length === 0) continue;

    const a = possibleA[randInt(0, possibleA.length - 1)];
    const yA = a <= h ? f1(a) : f2(a);

    // dodatkowo: żeby nie było super trywialne typu yA = v zawsze,
    // unikamy zbyt częstego a=h
    if (a === h && possibleA.length >= 2 && randInt(0, 3) !== 0) {
      // wybierz inne a, jeśli się da
      const alt = possibleA.filter((x) => x !== h);
      if (alt.length) {
        const a2 = alt[randInt(0, alt.length - 1)];
        const yA2 = a2 <= h ? f1(a2) : f2(a2);
        // podmień
        // eslint-disable-next-line no-unused-vars
        const _ = yA2;
        // reassign
        // (robimy bezpośrednio)
        // NOTE: JS const, więc robimy osobno:
        // return z a2
        const eps = 0;

        const points = {
          A: {
            x: xL,
            y: yL,
            markerSymbol: leftClosed ? "circle-filled" : "circle-open",
            showLabel: false,
          },
          AL: {
            x: xL + eps,
            y: f1(xL + eps),
            showDot: false,
            showMarker: false,
            showLabel: false,
            label: "",
          },
          B: {
            x: h,
            y: v,
            showDot: false,
            showMarker: false,
            showLabel: false,
          },
          CR: {
            x: xR - eps,
            y: f2(xR - eps),
            showDot: false,
            showMarker: false,
            showLabel: false,
            label: "",
          },
          C: {
            x: xR,
            y: yR,
            markerSymbol: rightClosed ? "circle-filled" : "circle-open",
            showLabel: false,
          },
        };

        return {
          points,
          segments: [
            { p1: "AL", p2: "B" },
            { p1: "B", p2: "CR" },
          ],
          xRange: [-6, 6],
          yRange: [-6, 6],
          a: a2,
          yA: yA2,
        };
      }
    }

    // standardowy return
    const eps = 0;

    const points = {
      A: {
        x: xL,
        y: yL,
        markerSymbol: leftClosed ? "circle-filled" : "circle-open",
        showLabel: false,
      },
      AL: {
        x: xL + eps,
        y: f1(xL + eps),
        showDot: false,
        showMarker: false,
        showLabel: false,
        label: "",
      },
      B: {
        x: h,
        y: v,
        showDot: false,
        showMarker: false,
        showLabel: false,
      },
      CR: {
        x: xR - eps,
        y: f2(xR - eps),
        showDot: false,
        showMarker: false,
        showLabel: false,
        label: "",
      },
      C: {
        x: xR,
        y: yR,
        markerSymbol: rightClosed ? "circle-filled" : "circle-open",
        showLabel: false,
      },
    };

    return {
      points,
      segments: [
        { p1: "AL", p2: "B" },
        { p1: "B", p2: "CR" },
      ],
      xRange: [-6, 6],
      yRange: [-6, 6],
      a,
      yA,
    };
  }

  return buildData();
}

function buildSmoothData() {
  for (let attempt = 0; attempt < 1000; attempt += 1) {
    const xL = randInt(-5, -3);
    const xR = randInt(3, 5);
    const a = randInt(xL + 1, xR - 1);
    const leftClosed = pickClosedFlag();
    const rightClosed = pickClosedFlag();
    const graphKind = Math.random() < 0.4 ? "linear" : "quadratic";

    let fn;
    if (graphKind === "linear") {
      const slope = randInt(1, 2) * (Math.random() < 0.5 ? -1 : 1);
      const yA = randInt(-3, 3);
      fn = (x) => slope * (x - a) + yA;
    } else {
      const coefficient = Math.random() < 0.5 ? -0.25 : 0.25;
      const vertexY = randInt(-2, 2);
      fn = (x) => coefficient * (x - a) * (x - a) + vertexY;
    }

    const yL = fn(xL);
    const yR = fn(xR);
    const yA = fn(a);
    if (![yL, yR, yA].every(Number.isInteger)) continue;
    if (Math.max(Math.abs(yL), Math.abs(yR), Math.abs(yA)) > 5) continue;

    return {
      graphKind,
      points: {
        A: {
          x: xL,
          y: yL,
          markerSymbol: leftClosed ? "circle-filled" : "circle-open",
          showLabel: false,
        },
        D: {
          x: xR,
          y: yR,
          markerSymbol: rightClosed ? "circle-filled" : "circle-open",
          showLabel: false,
        },
      },
      curves: [samplePolynomialCurve(fn, xL, xR, [a])],
      xRange: [-6, 6],
      yRange: [-6, 6],
      a,
      yA,
    };
  }
  return buildSmoothData();
}

/* ===================== API ===================== */

export function generateTask() {
  return {
    type: "ReadFunctionValueFromGraphTask",
    data: buildSmoothData(),
  };
}

export function renderTask({ data }) {
  const sizePx = 330;

  return (
    <div className="flex flex-col gap-3">
      <p>
        Na wykresie przedstawiono funkcję. Odczytaj wartość{" "}
        <InlineMath math={`f(${data.a})`} />.
      </p>

      <div style={{ margin: "0 auto", maxWidth: sizePx }}>
        <GraphViewer
          width={sizePx}
          points={data.points}
          curves={data.curves}
          xRange={data.xRange}
          yRange={data.yRange}
        />
      </div>

      <div>
        <InlineMath math={`f(${data.a}) =`} /> <InlineMath math={"\\_\\_\\_\\_"} />.
      </div>
    </div>
  );
}

export function renderInput(task, userAnswer, onChange, disabled) {
  const a = task?.data?.a;

  return (
    <div className="flex flex-col gap-2">
      <div>
        <strong>Odpowiedź:</strong>
      </div>
      <div><InlineMath math={`f(${a})=`} /></div>

      <div className="max-w-[260px]">
        <FractionInput
          value={userAnswer || ""}
          onChange={onChange}
          disabled={disabled}
          placeholder="np. 3 lub -1/2"
          label={`f(${a})`}
        />
      </div>

      {/* Podgląd tylko jeśli jest raw */}
      {userAnswer &&
      typeof userAnswer === "object" &&
      typeof userAnswer.raw === "string" &&
      userAnswer.raw.trim() ? (
        <div>
          <InlineMath math={`f(${a}) = ${userAnswer.raw}`} />
        </div>
      ) : null}
    </div>
  );
}

export function checkAnswer({ data }, userAnswer) {
  if (!userAnswer || typeof userAnswer !== "object") return false;
  const parsed = userAnswer.parsed;
  if (!Number.isFinite(parsed)) return false;

  return eq(parsed, data.yA);
}

export function renderAnswer({ data }) {
  return <InlineMath math={`f(${data.a}) = ${data.yA}`} />;
}

export function renderExplanation({ data }) {
  return (
    <div style={{ textAlign: "left", lineHeight: 1.7 }}>
      <div>
        <strong>Krok 1.</strong> Na osi <InlineMath math={"x"} /> znajdź argument{" "}
        <InlineMath math={`${data.a}`} />.
      </div>

      <div style={{ marginTop: 8 }}>
        <strong>Krok 2.</strong> Idź pionowo do wykresu i odczytaj z osi{" "}
        <InlineMath math={"y"} /> odpowiadającą wartość.
      </div>

      <div style={{ marginTop: 10 }}>
        <BlockMath math={`f(${data.a}) = ${data.yA}`} />
      </div>
    </div>
  );
}

export function renderUserAnswer(userAnswer, task) {
  const a = task?.data?.a;
  if (!userAnswer || typeof userAnswer !== "object" || !userAnswer.raw || !String(userAnswer.raw).trim()) {
    return <i>(brak odpowiedzi)</i>;
  }
  return <InlineMath math={`f(${a}) = ${userAnswer.raw}`} />;
}

export default {
  generateTask,
  renderTask,
  renderInput,
  checkAnswer,
  renderAnswer,
  renderExplanation,
  renderUserAnswer,
};
